const NOTIFICATIONS_DB = 'notifications-db';
const NOTIFICATIONS_STORE = 'notifications';
const NOTIFICATIONS_SECTIONS_STORE = 'notification-sections';

/**
 * Abre la DB y asegura que el store exista, luego ejecuta el callback.
 * @param {string} storeName - Nombre del store
 * @param {'readonly'|'readwrite'} mode - Modo de transacción
 * @param {(store: IDBObjectStore, resolve: Function) => void} callback - Función a ejecutar con el store
 * @param {Function} resolve - Función para resolver la promesa
 * @param {any} defaultValue - Valor por defecto en caso de error
 */
const withStore = (storeName, mode, callback, resolve, defaultValue) => {
  const request = indexedDB.open(NOTIFICATIONS_DB);

  request.onupgradeneeded = function (event) {
    const db = event.target.result;
    if (!db.objectStoreNames.contains(storeName)) {
      db.createObjectStore(storeName, { keyPath: 'id' });
    }
  };

  request.onsuccess = function (event) {
    const db = event.target.result;

    if (!db.objectStoreNames.contains(storeName)) {
      const currentVersion = db.version;
      db.close();

      const upgradeReq = indexedDB.open(NOTIFICATIONS_DB, currentVersion + 1);
      upgradeReq.onupgradeneeded = function (ev) {
        const upDb = ev.target.result;
        if (!upDb.objectStoreNames.contains(storeName)) {
          upDb.createObjectStore(storeName, { keyPath: 'id' });
        }
      };
      upgradeReq.onsuccess = function (ev) {
        const upDb = ev.target.result;
        const tx = upDb.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        callback(store, resolve);
        tx.oncomplete = function () {
          upDb.close();
        };
      };
      upgradeReq.onerror = function () {
        resolve(defaultValue);
      };
      return;
    }

    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    callback(store, resolve);
    tx.oncomplete = function () {
      db.close();
    };
  };

  request.onerror = function () {
    resolve(defaultValue);
  };
};

// ==================== Notifications ====================

export const saveNotificationToIDB = (notification) => {
  return new Promise((resolve) => {
    withStore(
      NOTIFICATIONS_STORE,
      'readwrite',
      (store) => {
        const getAll = store.getAll();
        getAll.onsuccess = function () {
          let notifications = getAll.result || [];
          notifications.unshift(notification);
          notifications = notifications.slice(0, 10);
          store.clear().onsuccess = function () {
            notifications.forEach((n) => store.put(n));
            resolve();
          };
        };
      },
      resolve,
      undefined
    );
  });
};

export const getNotificationsFromIDB = () => {
  return new Promise((resolve) => {
    withStore(
      NOTIFICATIONS_STORE,
      'readonly',
      (store, res) => {
        const getAll = store.getAll();
        getAll.onsuccess = function () {
          res(getAll.result || []);
        };
        getAll.onerror = function () {
          res([]);
        };
      },
      resolve,
      []
    );
  });
};

export const deleteNotificationFromIDB = (id) => {
  return new Promise((resolve) => {
    withStore(
      NOTIFICATIONS_STORE,
      'readwrite',
      (store) => {
        store.delete(id);
        resolve();
      },
      resolve,
      undefined
    );
  });
};

export const toggleNotificationReadStatusInIDB = (id) => {
  return new Promise((resolve) => {
    withStore(
      NOTIFICATIONS_STORE,
      'readwrite',
      (store) => {
        const getReq = store.get(id);
        getReq.onsuccess = function () {
          const notification = getReq.result;
          if (notification) {
            notification.read = !notification.read;
            store.put(notification);
          }
          resolve();
        };
        getReq.onerror = function () {
          resolve();
        };
      },
      resolve,
      undefined
    );
  });
};

// ==================== Selected Sections ====================

export const saveSelectedSectionsToIDB = (slugs) => {
  return new Promise((resolve) => {
    withStore(
      NOTIFICATIONS_SECTIONS_STORE,
      'readwrite',
      (store) => {
        store.put({ id: 'selected', slugs: slugs || [] });
        resolve();
      },
      resolve,
      undefined
    );
  });
};

export const getSelectedSectionsFromIDB = () => {
  return new Promise((resolve) => {
    withStore(
      NOTIFICATIONS_SECTIONS_STORE,
      'readonly',
      (store, res) => {
        const getReq = store.get('selected');
        getReq.onsuccess = function () {
          res((getReq.result && getReq.result.slugs) || []);
        };
        getReq.onerror = function () {
          res([]);
        };
      },
      resolve,
      []
    );
  });
};

// ==================== LocalStorage Helpers ====================

const STORAGE_KEYS = {
  FCM_TOKEN: 'notifications_fcm_token',
  INITIAL_SUBSCRIPTION_DONE: 'notifications_initial_subscription_done',
  SELECTED_TOPICS: 'notifications_selected_topics',
};

/**
 * Guarda el token FCM en localStorage
 */
export const saveFcmToken = (token) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.FCM_TOKEN, token);
  }
};

/**
 * Obtiene el token FCM de localStorage
 */
export const getFcmToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(STORAGE_KEYS.FCM_TOKEN);
  }
  return null;
};

/**
 * Marca que la suscripción inicial ya fue realizada
 */
export const setInitialSubscriptionDone = () => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.INITIAL_SUBSCRIPTION_DONE, 'true');
  }
};

/**
 * Verifica si la suscripción inicial ya fue realizada
 */
export const isInitialSubscriptionDone = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(STORAGE_KEYS.INITIAL_SUBSCRIPTION_DONE) === 'true';
  }
  return false;
};

/**
 * Guarda los topics seleccionados en localStorage
 */
export const saveSelectedTopics = (topics) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.SELECTED_TOPICS, JSON.stringify(topics));
  }
};

/**
 * Obtiene los topics seleccionados de localStorage
 */
export const getSelectedTopics = () => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_TOPICS);
    return stored ? JSON.parse(stored) : [];
  }
  return [];
};